import React, { useEffect, useState, useRef, useCallback } from "react";
import {
    Card, Row, Col, Tag, Button, Drawer, Table, Typography, Badge,
    Progress, Spin, Tooltip, Empty, Modal, Divider
} from "antd";
import {
    AlertOutlined, SafetyCertificateOutlined, ReloadOutlined,
    FileTextOutlined, ClockCircleOutlined, RocketOutlined,
    WarningOutlined, BugOutlined, ThunderboltOutlined, EyeOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../configs";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const { Text } = Typography;

const CATEGORY_META = {
    ADJ: { label: "Adjustment Mencurigakan", icon: <WarningOutlined />, color: "#ff4d4f", bg: "#fff1f0" },
    OFFHOUR: { label: "Di Luar Jam Kerja", icon: <ClockCircleOutlined />, color: "#fa8c16", bg: "#fff7e6" },
    EDIT_FREQ: { label: "Dokumen Sering Diedit", icon: <BugOutlined />, color: "#fa8c16", bg: "#fff7e6" },
    BURST: { label: "Pengeluaran Mendadak", icon: <ThunderboltOutlined />, color: "#ff4d4f", bg: "#fff1f0" },
    DISCREP: { label: "Stok Keluar > Masuk", icon: <AlertOutlined />, color: "#ff4d4f", bg: "#fff1f0" },
    GHOST: { label: "Stok Idle Lama", icon: <EyeOutlined />, color: "#faad14", bg: "#fffbe6" },
    ITEM_MISMATCH: { label: "Kode Barang Salah", icon: <BugOutlined />, color: "#eb2f96", bg: "#fff0f6" },
    USER_ERR: { label: "User Sering Koreksi", icon: <WarningOutlined />, color: "#722ed1", bg: "#f9f0ff" },
};

const SEV_COLOR = { HIGH: "error", MEDIUM: "warning", LOW: "gold" };
const ERR_COLOR = { HIGH: "#ff4d4f", MEDIUM: "#fa8c16", LOW: "#722ed1" };
const RISK_HEX = s => s >= 60 ? "#ff4d4f" : s >= 25 ? "#fa8c16" : "#52c41a";
const RISK_ST = s => s >= 60 ? "exception" : s >= 25 ? "normal" : "success";

export default function AntiFraud() {
    const [alerts, setAlerts] = useState([]);
    const [riskScores, setRiskScores] = useState([]);
    const [userErrorList, setUserErrorList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scannedAt, setScannedAt] = useState(null);
    const [countdown, setCountdown] = useState(3 * 60 * 60); // 3 jam dalam detik
    const [drawer, setDrawer] = useState({ open: false, alert: null });
    const [userDrawer, setUserDrawer] = useState({ open: false, user: null, logs: [], mismatch: [], loading: false });
    const [genLoading, setGenLoading] = useState(false);
    // null = belum pernah dipanggil, true = aktif, false = mati
    const [aiStatus, setAiStatus] = useState({ available: null, error: null, checkedAt: null });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const intervalRef = useRef(null);
    const countdownRef = useRef(null);
    const resizeRef = useRef(null);

    const fetchScan = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await axiosInstance.get("/antifraud/scan");
            setAlerts(res.data.alerts || []);
            setRiskScores(res.data.riskScores || []);
            setUserErrorList(res.data.userErrorList || []);
            if (res.data.aiStatus) setAiStatus(res.data.aiStatus);
            setScannedAt(new Date());
            setCountdown(3 * 60 * 60);
        } catch (e) {
            console.error("antifraud scan error", e);
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchScan();
        intervalRef.current = setInterval(() => fetchScan(true), 3 * 60 * 60 * 1000); // 3 jam
        countdownRef.current = setInterval(() => setCountdown(c => (c > 0 ? c - 1 : 3 * 60 * 60)), 1000);
        // responsive listener
        const onResize = () => setIsMobile(window.innerWidth < 768);
        resizeRef.current = onResize;
        window.addEventListener('resize', onResize);
        return () => {
            clearInterval(intervalRef.current);
            clearInterval(countdownRef.current);
            window.removeEventListener('resize', resizeRef.current);
        };
    }, [fetchScan]);

    const handleGenerateReport = async () => {
        setGenLoading(true);
        try {
            const res = await axiosInstance.post("/antifraud/report", {}, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
            window.open(url, "_blank");
        } catch (e) {
            Modal.error({ title: "Gagal generate laporan", content: e.message });
        } finally {
            setGenLoading(false);
        }
    };

    const handleUserCardClick = async (u) => {
        setUserDrawer({ open: true, user: u, logs: [], mismatch: [], loading: true });
        try {
            const res = await axiosInstance.get(`/antifraud/user-detail/${u.id_user}`);
            setUserDrawer(prev => ({ ...prev, logs: res.data.logs || [], mismatch: res.data.mismatch || [], loading: false }));
        } catch (e) {
            setUserDrawer(prev => ({ ...prev, loading: false }));
        }
    };

    const highCount = alerts.filter(a => a.severity === "HIGH").length;
    const medCount = alerts.filter(a => a.severity === "MEDIUM").length;
    const lowCount = alerts.filter(a => a.severity === "LOW").length;
    const highRisk = riskScores.filter(r => r.level === "HIGH").length;

    // ─── Alert Row ──────────────────────────────────────
    const AlertRow = ({ a }) => {
        const meta = CATEGORY_META[a.category] || {};
        return (
            <div
                onClick={() => setDrawer({ open: true, alert: a })}
                style={{
                    display: "flex", alignItems: "flex-start", gap: 10,
                    padding: "10px 14px", marginBottom: 5, borderRadius: 10, cursor: "pointer",
                    background: "#fff",
                    borderLeft: `4px solid ${meta.color || "#ddd"}`,
                    boxShadow: "0 1px 4px rgba(0,0,0,.06)",
                    transition: "transform .15s, box-shadow .15s",
                    animation: "slideIn .3s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateX(4px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,.1)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,.06)"; }}
            >
                <div style={{ color: meta.color, fontSize: 18, paddingTop: 2, flexShrink: 0 }}>{meta.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                        <Tag color={SEV_COLOR[a.severity]} style={{ margin: 0, fontSize: 10 }}>{a.severity}</Tag>
                        <span style={{ fontSize: 11, fontWeight: 700, color: meta.color }}>{a.category}</span>
                        <span style={{ fontSize: 10, color: "#bbb", marginLeft: "auto" }}>{a.ts ? dayjs(a.ts).fromNow() : ""}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#333", lineHeight: 1.4 }}>{a.description}</div>
                    {a.customer_name && <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>👤 {a.customer_name}</div>}
                </div>
            </div>
        );
    };

    return (
        <div style={{ padding: isMobile ? "10px 10px" : "20px 24px", minHeight: "100vh", background: "#f0f2f5", fontFamily: "Poppins, Inter, sans-serif" }}>
            <style>{`
              @keyframes slideIn { from { opacity:0; transform:translateX(-8px); } to { opacity:1; transform:translateX(0); } }
              @keyframes pulse { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:.6;transform:scale(1.3);} }
              @keyframes fadeUp { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:translateY(0);} }
            `}</style>

            {/* ══ HEADER BAR ══════════════════════════════════════════════════ */}
            <div style={{
                background: "linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)",
                borderRadius: isMobile ? 12 : 16,
                padding: isMobile ? "14px 16px" : "18px 24px",
                marginBottom: 16,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "flex-start" : "center",
                justifyContent: "space-between",
                gap: isMobile ? 12 : 0,
                boxShadow: "0 8px 32px rgba(26,35,126,.25)",
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{
                        width: isMobile ? 38 : 50,
                        height: isMobile ? 38 : 50,
                        borderRadius: 14,
                        background: "rgba(255,255,255,.15)", backdropFilter: "blur(10px)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: isMobile ? 20 : 26, border: "1px solid rgba(255,255,255,.2)",
                        flexShrink: 0,
                    }}>🛡</div>
                    <div>
                        <div style={{ fontSize: isMobile ? 15 : 20, fontWeight: 800, color: "#fff", letterSpacing: "-.3px" }}>
                            Monitoring Anti Fraud
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
                            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#69db7c", display: "inline-block", animation: "pulse 2s infinite", flexShrink: 0 }} />
                            <span style={{ color: "rgba(255,255,255,.75)", fontSize: isMobile ? 10 : 12 }}>
                                {scannedAt ? `Scan ${dayjs(scannedAt).fromNow()}` : "Initializing…"}
                                {!isMobile && <>&nbsp;·&nbsp;Refresh dalam <b style={{ color: "#fff" }}>{`${String(Math.floor(countdown / 3600)).padStart(2, '0')}:${String(Math.floor((countdown % 3600) / 60)).padStart(2, '0')}:${String(countdown % 60).padStart(2, '0')}`}</b></>}
                            </span>
                        </div>
                    </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: isMobile ? "flex-start" : "flex-end", width: isMobile ? "100%" : "auto" }}>
                    {/* ── AI Status Badge ── */}
                    {aiStatus.available !== null && (
                        <Tooltip title={
                            aiStatus.available
                                ? `Claude aktif — model: ${aiStatus.model || "claude"}`
                                : `Claude tidak tersedia: ${aiStatus.error || "timeout/model error"}`
                        }>
                            <div style={{
                                display: "inline-flex", alignItems: "center", gap: 5,
                                background: aiStatus.available ? "rgba(82,196,26,.2)" : "rgba(255,77,79,.2)",
                                border: `1px solid ${aiStatus.available ? "#52c41a" : "#ff4d4f"}`,
                                borderRadius: 20, padding: "2px 10px", fontSize: 11, cursor: "help",
                            }}>
                                <span style={{
                                    width: 6, height: 6, borderRadius: "50%",
                                    background: aiStatus.available ? "#52c41a" : "#ff4d4f",
                                    display: "inline-block",
                                    animation: aiStatus.available ? "pulse 2s infinite" : "none",
                                }} />
                                <span style={{ color: "#fff", fontWeight: 600 }}>
                                    {aiStatus.available ? "AI Aktif" : "⚠ AI Mati"}
                                </span>
                            </div>
                        </Tooltip>
                    )}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", width: isMobile ? "100%" : "auto" }}>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => fetchScan()}
                            loading={loading}
                            size={isMobile ? "middle" : "middle"}
                            style={{
                                background: "rgba(255,255,255,.15)",
                                border: "1px solid rgba(255,255,255,.3)",
                                color: "#fff", backdropFilter: "blur(6px)",
                                fontWeight: 600,
                                flex: isMobile ? 1 : "none",
                            }}
                        >{isMobile ? "Scan" : "Scan Sekarang"}</Button>
                        <Tooltip title={!aiStatus.available && aiStatus.available !== null ? `⚠ AI tidak aktif — laporan akan tampil tanpa narasi AI. Alasan: ${aiStatus.error}` : ""}>
                            <Button
                                type="primary"
                                icon={<FileTextOutlined />}
                                loading={genLoading}
                                onClick={handleGenerateReport}
                                style={{
                                    background: !aiStatus.available && aiStatus.available !== null ? "#ff4d4f" : "#fff",
                                    color: !aiStatus.available && aiStatus.available !== null ? "#fff" : "#1a237e",
                                    border: "none", fontWeight: 700,
                                    flex: isMobile ? 1 : "none",
                                }}
                            >
                                {isMobile ? "Laporan" : (!aiStatus.available && aiStatus.available !== null ? "⚠ Generate Laporan (AI Mati)" : "Generate Laporan AI")}
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            </div>

            {/* ══ STAT ROW ════════════════════════════════════════════════════ */}
            <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
                {[
                    { label: "Total Alert", value: alerts.length, color: "#1a237e", icon: "🔔" },
                    { label: "High Risk", value: highCount, color: "#ff4d4f", icon: "🔴" },
                    { label: "Medium Risk", value: medCount, color: "#fa8c16", icon: "🟠" },
                    { label: "Low Risk", value: lowCount, color: "#faad14", icon: "🟡" },
                    { label: "Importir Berisiko", value: highRisk, color: "#722ed1", icon: "⚠️" },
                    { label: "User Errors", value: userErrorList.length, color: "#eb2f96", icon: "👤" },
                ].map(s => (
                    <Col xs={12} sm={8} md={4} key={s.label}>
                        <div style={{
                            background: "#fff", borderRadius: 14, padding: "14px 16px",
                            boxShadow: "0 2px 12px rgba(0,0,0,.06)",
                            borderTop: `4px solid ${s.color}`,
                            animation: "fadeUp .4s ease",
                        }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <div style={{ fontSize: 26, fontWeight: 900, color: s.color }}>{s.value}</div>
                                <div style={{ fontSize: 22 }}>{s.icon}</div>
                            </div>
                            <div style={{ fontSize: 11, color: "#888", marginTop: 4, fontWeight: 500 }}>{s.label}</div>
                        </div>
                    </Col>
                ))}
            </Row>

            {/* ══ MAIN TWO-PANEL ══════════════════════════════════════════════ */}
            <Row gutter={[16, 16]}>
                {/* LEFT: Risk Scores */}
                <Col xs={24} lg={8}>
                    <Card
                        bordered={false}
                        style={{ borderRadius: 16, boxShadow: "0 2px 16px rgba(0,0,0,.07)", height: "100%" }}
                        bodyStyle={{ padding: 16 }}
                        title={
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <SafetyCertificateOutlined style={{ color: "#1a237e", fontSize: 16 }} />
                                <span style={{ fontWeight: 700, fontSize: 13 }}>Risk Score Importir</span>
                                <Tag style={{ marginLeft: "auto", fontSize: 10 }}>{riskScores.length} importir</Tag>
                            </div>
                        }
                    >
                        <div style={{ maxHeight: 520, overflowY: "auto", paddingRight: 4 }}>
                            {loading ? (
                                <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
                            ) : riskScores.length === 0 ? (
                                <Empty description="Tidak ada risiko terdeteksi" />
                            ) : riskScores.map((r, i) => (
                                <div key={i} style={{
                                    marginBottom: 12, padding: "10px 14px", borderRadius: 12,
                                    background: r.score >= 60 ? "#fff1f0" : r.score >= 25 ? "#fff7e6" : "#f6ffed",
                                    border: `1px solid ${RISK_HEX(r.score)}22`,
                                }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                                        <Text strong style={{ fontSize: 12, maxWidth: "70%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {r.customer_name}
                                        </Text>
                                        <Tag color={SEV_COLOR[r.level]} style={{ fontSize: 10, margin: 0 }}>{r.level}</Tag>
                                    </div>
                                    <Progress
                                        percent={r.score}
                                        status={RISK_ST(r.score)}
                                        strokeColor={RISK_HEX(r.score)}
                                        size="small"
                                        format={p => <span style={{ fontSize: 11, color: RISK_HEX(r.score), fontWeight: 700 }}>{p}</span>}
                                    />
                                    <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 3 }}>
                                        {r.flags.map(f => (
                                            <span key={f} style={{ fontSize: 10, background: "rgba(0,0,0,.05)", borderRadius: 4, padding: "1px 6px", color: "#666" }}>{f}</span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </Col>

                {/* RIGHT: Live Alert Feed */}
                <Col xs={24} lg={16}>
                    <Card
                        bordered={false}
                        style={{ borderRadius: 16, boxShadow: "0 2px 16px rgba(0,0,0,.07)" }}
                        bodyStyle={{ padding: "12px 14px", maxHeight: 580, overflowY: "auto" }}
                        title={
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <AlertOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
                                <span style={{ fontWeight: 700, fontSize: 13 }}>Live Alert Feed</span>
                                <Badge
                                    count={alerts.length}
                                    style={{ background: alerts.length > 0 ? "#ff4d4f" : "#52c41a", marginLeft: 4 }}
                                />
                            </div>
                        }
                    >
                        {loading ? (
                            <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>
                        ) : alerts.length === 0 ? (
                            <div style={{ textAlign: "center", padding: 40 }}>
                                <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>
                                <Text type="secondary">Tidak ada anomali terdeteksi. Sistem berjalan normal.</Text>
                            </div>
                        ) : (
                            <>
                                {["HIGH", "MEDIUM", "LOW"].map(sev => {
                                    const grp = alerts.filter(a => a.severity === sev);
                                    if (!grp.length) return null;
                                    return (
                                        <div key={sev}>
                                            <Divider orientation="left" style={{ fontSize: 11, margin: "8px 0" }}>
                                                <Tag color={SEV_COLOR[sev]} style={{ fontSize: 11 }}>{sev} ({grp.length})</Tag>
                                            </Divider>
                                            {grp.map((a, i) => <AlertRow key={i} a={a} />)}
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* ══ CATEGORY BREAKDOWN ══════════════════════════════════════════ */}
            <Card
                bordered={false}
                style={{ marginTop: 16, borderRadius: 16, boxShadow: "0 2px 16px rgba(0,0,0,.07)" }}
                bodyStyle={{ padding: 16 }}
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <RocketOutlined style={{ color: "#722ed1" }} />
                        <span style={{ fontWeight: 700, fontSize: 13 }}>Breakdown per Kategori</span>
                    </div>
                }
            >
                <Row gutter={[10, 10]}>
                    {Object.entries(CATEGORY_META).map(([cat, meta]) => {
                        const cnt = alerts.filter(a => a.category === cat).length;
                        return (
                            <Col xs={12} sm={6} md={3} key={cat}>
                                <div style={{
                                    background: cnt > 0 ? meta.bg : "#fafafa",
                                    border: `1.5px solid ${cnt > 0 ? meta.color : "#eee"}55`,
                                    borderRadius: 12, padding: "12px 10px", textAlign: "center",
                                    transition: "all .2s",
                                }}>
                                    <div style={{ fontSize: 22, color: cnt > 0 ? meta.color : "#ddd", marginBottom: 4 }}>{meta.icon}</div>
                                    <div style={{ fontSize: 26, fontWeight: 900, color: cnt > 0 ? meta.color : "#ccc", lineHeight: 1 }}>{cnt}</div>
                                    <div style={{ fontSize: 10, color: "#888", marginTop: 4, lineHeight: 1.3 }}>{meta.label}</div>
                                </div>
                            </Col>
                        );
                    })}
                </Row>
            </Card>

            {/* ══ USER ERROR LEADERBOARD ══════════════════════════════════════ */}
            <Card
                bordered={false}
                style={{ marginTop: 16, borderRadius: 16, boxShadow: "0 2px 16px rgba(0,0,0,.07)" }}
                bodyStyle={{ padding: 16 }}
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <BugOutlined style={{ color: "#722ed1" }} />
                        <span style={{ fontWeight: 700, fontSize: 13 }}>Pengguna Rawan Kesalahan Input</span>
                        <Tag color="purple" style={{ fontSize: 10, marginLeft: 4 }}>30 hari terakhir</Tag>
                    </div>
                }
            >
                {loading ? (
                    <div style={{ textAlign: "center", padding: 30 }}><Spin /></div>
                ) : userErrorList.length === 0 ? (
                    <div style={{ textAlign: "center", padding: 24 }}>
                        <div style={{ fontSize: 36 }}>🎉</div>
                        <Text type="secondary" style={{ fontSize: 13 }}>Tidak ada pola kesalahan terdeteksi.</Text>
                    </div>
                ) : (
                    <Row gutter={[12, 12]}>
                        {userErrorList.map((u, i) => (
                            <Col xs={24} sm={12} lg={8} key={i}>
                                <div
                                    onClick={() => handleUserCardClick(u)}
                                    style={{
                                        background: "#fff",
                                        border: `1.5px solid ${ERR_COLOR[u.level]}33`,
                                        borderRadius: 14, padding: "14px 16px",
                                        cursor: "pointer", position: "relative",
                                        boxShadow: "0 2px 10px rgba(0,0,0,.05)",
                                        transition: "transform .15s, box-shadow .15s",
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,.12)"; }}
                                    onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,.05)"; }}
                                >
                                    {/* Rank watermark */}
                                    <div style={{ position: "absolute", top: 10, right: 14, fontSize: 28, fontWeight: 900, color: ERR_COLOR[u.level], opacity: .08 }}>#{i + 1}</div>

                                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                                        <div style={{
                                            width: 40, height: 40, borderRadius: "50%",
                                            background: `linear-gradient(135deg, ${ERR_COLOR[u.level]}, ${ERR_COLOR[u.level]}99)`,
                                            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                                            fontWeight: 900, fontSize: 16, flexShrink: 0,
                                            boxShadow: `0 4px 12px ${ERR_COLOR[u.level]}44`,
                                        }}>
                                            {(u.user_name || u.username || "?")[0].toUpperCase()}
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {u.user_name || u.username}
                                            </div>
                                            <div style={{ fontSize: 11, color: "#aaa" }}>@{u.username}</div>
                                        </div>
                                    </div>

                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                        <Tag color={u.level === "HIGH" ? "error" : u.level === "MEDIUM" ? "warning" : "purple"}>{u.level}</Tag>
                                        <span style={{ fontSize: 24, fontWeight: 900, color: ERR_COLOR[u.level] }}>
                                            {u.total}
                                            <span style={{ fontSize: 11, fontWeight: 400, color: "#bbb", marginLeft: 3 }}>koreksi</span>
                                        </span>
                                    </div>

                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                        {Object.entries(u.breakdown).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([mod, cnt]) => (
                                            <Tooltip key={mod} title={`${cnt} koreksi di ${mod}`}>
                                                <span style={{
                                                    fontSize: 10, background: `${ERR_COLOR[u.level]}11`,
                                                    border: `1px solid ${ERR_COLOR[u.level]}33`,
                                                    borderRadius: 6, padding: "1px 8px", color: ERR_COLOR[u.level], cursor: "default", fontWeight: 600
                                                }}>
                                                    {mod} {cnt}
                                                </span>
                                            </Tooltip>
                                        ))}
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                )}
                <div style={{ marginTop: 12, fontSize: 11, color: "#ccc" }}>
                    ⚠️ Dihitung dari aktivitas DELETE/UPDATE/PUT + kode barang salah di pembongkaran.
                </div>
            </Card>

            {/* ══ USER DETAIL DRAWER ══════════════════════════════════════════ */}
            <Drawer
                title={
                    userDrawer.user && (
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{
                                width: 34, height: 34, borderRadius: "50%",
                                background: `linear-gradient(135deg, ${ERR_COLOR[userDrawer.user?.level]}, ${ERR_COLOR[userDrawer.user?.level]}88)`,
                                color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                                fontWeight: 800, fontSize: 14,
                            }}>
                                {(userDrawer.user?.user_name || "?")[0].toUpperCase()}
                            </div>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>
                                    Suspect Detail — {userDrawer.user?.user_name}
                                </div>
                                <div style={{ fontSize: 11, color: "#999" }}>@{userDrawer.user?.username}</div>
                            </div>
                        </div>
                    )
                }
                width={640}
                open={userDrawer.open}
                onClose={() => setUserDrawer({ open: false, user: null, logs: [], mismatch: [], loading: false })}
                destroyOnClose
                bodyStyle={{ padding: "16px 20px", background: "#f8f9fa" }}
            >
                {userDrawer.loading ? (
                    <div style={{ textAlign: "center", padding: 80 }}><Spin size="large" /></div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

                        {/* Summary header */}
                        <div style={{
                            background: "#fff", borderRadius: 14, padding: "14px 18px",
                            boxShadow: "0 2px 12px rgba(0,0,0,.06)",
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            borderLeft: `5px solid ${ERR_COLOR[userDrawer.user?.level] || "#722ed1"}`,
                        }}>
                            <div>
                                <Text strong style={{ fontSize: 16 }}>{userDrawer.user?.user_name}</Text>
                                <div style={{ fontSize: 12, color: "#aaa" }}>@{userDrawer.user?.username}</div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                                <Tag color={userDrawer.user?.level === "HIGH" ? "error" : userDrawer.user?.level === "MEDIUM" ? "warning" : "purple"}>
                                    {userDrawer.user?.level}
                                </Tag>
                                <div style={{ fontSize: 28, fontWeight: 900, color: ERR_COLOR[userDrawer.user?.level], lineHeight: 1 }}>
                                    {userDrawer.user?.total}
                                    <span style={{ fontSize: 12, fontWeight: 400, color: "#bbb", marginLeft: 4 }}>koreksi</span>
                                </div>
                            </div>
                        </div>

                        {/* Item mismatch */}
                        {userDrawer.mismatch.length > 0 && (
                            <div style={{ background: "#fff", borderRadius: 14, padding: "14px 18px", boxShadow: "0 2px 12px rgba(0,0,0,.06)" }}>
                                <div style={{ fontWeight: 700, fontSize: 13, color: "#ff4d4f", marginBottom: 10 }}>
                                    ⚠️ Kode Barang Tidak Valid di Stripping
                                </div>
                                <Table
                                    dataSource={userDrawer.mismatch}
                                    rowKey={(_, i) => i}
                                    size="small"
                                    pagination={false}
                                    columns={[
                                        { title: "Kode (Salah)", dataIndex: "item_code", key: "ic", render: t => <Tag color="error">{t}</Tag> },
                                        { title: "No. Stripping", dataIndex: "stripping_number", key: "no", width: 130 },
                                        { title: "Frekuensi", dataIndex: "occurrence", key: "occ", width: 80, render: n => <b style={{ color: "#ff4d4f" }}>{n}×</b> },
                                    ]}
                                />
                            </div>
                        )}

                        {/* Activity log */}
                        <div style={{ background: "#fff", borderRadius: 14, padding: "14px 18px", boxShadow: "0 2px 12px rgba(0,0,0,.06)" }}>
                            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>📋 Riwayat Koreksi / Penghapusan (30 Hari)</div>
                            <Table
                                dataSource={userDrawer.logs}
                                rowKey="id_user_log"
                                size="small"
                                pagination={{ pageSize: 10, size: "small" }}
                                columns={[
                                    {
                                        title: "Tanggal", key: "tgl", width: 135,
                                        render: (_, r) => (
                                            <span style={{ fontSize: 11 }}>
                                                {r.tgl ? dayjs(r.tgl).format("DD MMM YYYY") : "-"}
                                                {r.waktu && <span style={{ color: "#999", marginLeft: 4 }}>{String(r.waktu).substring(0, 8)}</span>}
                                            </span>
                                        )
                                    },
                                    {
                                        title: "Aksi", dataIndex: "activity", key: "activity", width: 80,
                                        render: t => <Tag color={t === "DELETE" ? "error" : "geekblue"} style={{ fontSize: 10, padding: "0 5px" }}>{t}</Tag>
                                    },
                                    { title: "Modul", dataIndex: "module", key: "mod", width: 110, render: t => <span style={{ fontSize: 11 }}>{t || "-"}</span> },
                                    { title: "Record ID", dataIndex: "record_id", key: "rid", render: t => <span style={{ fontSize: 11, color: "#1890ff" }}>{t || "-"}</span> },
                                ]}
                            />
                            {userDrawer.logs.length === 0 && <Text type="secondary" style={{ fontSize: 12 }}>Tidak ada log koreksi ditemukan.</Text>}
                        </div>

                        {/* Analysis panel */}
                        {userDrawer.user && (() => {
                            const u = userDrawer.user;
                            const logs = userDrawer.logs;
                            const breakdown = u?.breakdown || {};
                            const topModules = Object.entries(breakdown).sort((a, b) => b[1] - a[1]);
                            const delCount = logs.filter(l => l.activity === "DELETE").length;
                            const updCount = logs.length - delCount;
                            const delRatio = logs.length > 0 ? Math.round((delCount / logs.length) * 100) : 0;
                            const hasMismatch = userDrawer.mismatch.length > 0;
                            const level = u?.level || "LOW";
                            const levelColor = ERR_COLOR[level];

                            const recs = [];
                            if (hasMismatch) recs.push("⚠️ Wajib verifikasi kode barang sebelum menyimpan di modul Stripping.");
                            if (delRatio > 40) recs.push("🗑️ Frekuensi DELETE tinggi — tambah review step sebelum finalisasi data.");
                            if (topModules[0]?.[0]) recs.push(`📂 Modul "${topModules[0][0]}" paling sering terdampak — perlu SOP / pelatihan khusus.`);
                            if (level === "HIGH") recs.push("🔴 Risiko tinggi — pendampingan supervisor saat input & audit berkala.");
                            else if (level === "MEDIUM") recs.push("🟠 Risiko menengah — monitor berkala & berikan feedback rutin.");
                            else recs.push("🟢 Risiko rendah — pantau agar pola koreksi tidak meningkat.");

                            return (
                                <div style={{
                                    background: `linear-gradient(135deg, ${levelColor}10 0%, #fff 100%)`,
                                    border: `1.5px solid ${levelColor}33`,
                                    borderRadius: 14, padding: "16px 18px",
                                }}>
                                    <div style={{ fontWeight: 800, fontSize: 14, color: levelColor, marginBottom: 14 }}>🔍 Analisis Temuan</div>

                                    {/* Mini stats */}
                                    <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                                        {[
                                            { label: "Total Koreksi", value: u?.total, color: levelColor },
                                            { label: "Penghapusan", value: `${delCount} (${delRatio}%)`, color: "#ff4d4f" },
                                            { label: "Edit/Update", value: updCount, color: "#1890ff" },
                                            { label: "Kode Salah", value: userDrawer.mismatch.length, color: "#fa8c16" },
                                        ].map(s => (
                                            <div key={s.label} style={{
                                                flex: "1 1 70px", background: "#fff", borderRadius: 10,
                                                padding: "8px 12px", textAlign: "center",
                                                boxShadow: `0 2px 8px ${s.color}22`,
                                                border: `1px solid ${s.color}22`,
                                            }}>
                                                <div style={{ fontSize: 20, fontWeight: 900, color: s.color }}>{s.value}</div>
                                                <div style={{ fontSize: 10, color: "#999" }}>{s.label}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Module bars */}
                                    {topModules.length > 0 && (
                                        <div style={{ marginBottom: 14 }}>
                                            <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 8 }}>Distribusi per Modul</div>
                                            {topModules.slice(0, 5).map(([mod, cnt]) => {
                                                const pct = Math.round((cnt / (u?.total || 1)) * 100);
                                                return (
                                                    <div key={mod} style={{ marginBottom: 7 }}>
                                                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
                                                            <span style={{ fontWeight: 600 }}>{mod}</span>
                                                            <span style={{ color: levelColor, fontWeight: 700 }}>{cnt}× &nbsp;<span style={{ color: "#bbb", fontWeight: 400 }}>({pct}%)</span></span>
                                                        </div>
                                                        <div style={{ background: "#f0f0f0", borderRadius: 6, height: 6 }}>
                                                            <div style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${levelColor}, ${levelColor}88)`, borderRadius: 6, height: "100%", transition: "width .6s ease" }} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Recommendations */}
                                    <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 8 }}>💡 Rekomendasi</div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                        {recs.map((r, i) => (
                                            <div key={i} style={{
                                                background: "#fff", borderRadius: 8, padding: "9px 13px",
                                                fontSize: 12, color: "#333",
                                                boxShadow: "0 1px 6px rgba(0,0,0,.06)",
                                                borderLeft: `3px solid ${levelColor}`,
                                                lineHeight: 1.6,
                                            }}>{r}</div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}
            </Drawer>

            {/* ══ ALERT DETAIL DRAWER ═════════════════════════════════════════ */}
            <Drawer
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ color: CATEGORY_META[drawer.alert?.category]?.color, fontSize: 18 }}>
                            {drawer.alert && CATEGORY_META[drawer.alert.category]?.icon}
                        </span>
                        <span>Detail Alert — <b>{drawer.alert?.category}</b></span>
                    </div>
                }
                width={520}
                open={drawer.open}
                onClose={() => setDrawer({ open: false, alert: null })}
                destroyOnClose
                bodyStyle={{ background: "#f8f9fa", padding: "16px 20px" }}
            >
                {drawer.alert && (() => {
                    const a = drawer.alert;
                    const meta = CATEGORY_META[a.category] || {};
                    return (
                        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                            <Tag color={SEV_COLOR[a.severity]} style={{ fontSize: 13, padding: "4px 14px", width: "fit-content" }}>
                                {a.severity} RISK
                            </Tag>
                            <div style={{
                                background: "#fff", borderRadius: 14, padding: "14px 18px",
                                borderLeft: `5px solid ${meta.color || "#ddd"}`,
                                boxShadow: "0 2px 12px rgba(0,0,0,.06)",
                                fontSize: 13, color: "#333", lineHeight: 1.6,
                            }}>
                                {a.description}
                            </div>
                            <div style={{ background: "#fff", borderRadius: 14, padding: "14px 18px", boxShadow: "0 2px 12px rgba(0,0,0,.06)" }}>
                                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Detail Data</div>
                                <Table
                                    dataSource={Object.entries(a.detail || {}).map(([k, v]) => ({ key: k, field: k, value: String(v) }))}
                                    columns={[
                                        { title: "Field", dataIndex: "field", key: "f", width: 140, render: t => <b style={{ color: "#555" }}>{t}</b> },
                                        { title: "Value", dataIndex: "value", key: "v" },
                                    ]}
                                    pagination={false}
                                    size="small"
                                    rowKey="key"
                                />
                            </div>
                            <div style={{ fontSize: 11, color: "#bbb", textAlign: "center" }}>
                                Terdeteksi: {a.ts ? dayjs(a.ts).format("DD MMM YYYY HH:mm:ss") : "-"}
                            </div>
                        </div>
                    );
                })()}
            </Drawer>
        </div>
    );
}
